import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Grid
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Layout from "../layout/layout";
import { useToast } from "../components/ToastContext";
import 'react-toastify/dist/ReactToastify.css';
import {api}from "../services/api";
import { useNavigate } from "react-router-dom";

const ApplicationFormPage = () => {
  const theme = useTheme();
  const toast = useToast();
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({
    OS: 1 // Default value for WindowsServer based on dropdownValues
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [vmSizes, setVmSizes] = useState([]);
  const [vmSizeSeries, setVmSizeSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [invalidFields, setInvalidFields] = useState({});

  useEffect(() => {
    const fetchFormProperties = async () => {
      setLoading(true);
      try {
        const response = await api.get("/api/Application/form/124/4");
        setFormFields(response.data.templateId);
      } catch (error) {
        console.error("Error fetching form properties:", error);
        setError("Failed to fetch form properties");
      } finally {
        setLoading(false);
      }
    };

    fetchFormProperties();
  }, []);

  const handleChange = async (fieldName, value) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: value }));
    setInvalidFields((prevInvalid) => ({ ...prevInvalid, [fieldName]: false })); // Reset invalid state

    if (fieldName === "location") {
      setVmSizeSeries([]); // Reset VM Size Series when location changes
      setVmSizes([]); // Reset VM Sizes when location changes
      setLoading(true);
      try {
        const response = await api.get(`/api/Application/vmSize/${encodeURIComponent(value)}`);
        setVmSizes(response.data);
      } catch (error) {
        console.error("Error fetching VM sizes:", error);
        setError("Failed to fetch VM sizes");
      } finally {
        setLoading(false);
      }
    }

    if (fieldName === "VMSizeSeries") {
      const location = formData['location'];
      if (location) {
        setLoading(true);
        try {
          const response = await api.get(
            `/api/Application/vmSize/${encodeURIComponent(location)}/${encodeURIComponent(value)}`);
          setVmSizeSeries(response.data);
        } catch (error) {
          console.error("Error fetching VM size series:", error);
          setError("Failed to fetch VM size series");
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const newInvalidFields = {};
    formFields.forEach((field) => {
      if (field.isRequired && (!formData[field.fieldName] || formData[field.fieldName] === "")) {
        newInvalidFields[field.fieldName] = true;
      }
    });

    if (Object.keys(newInvalidFields).length > 0) {
      setLoading(false);
      setInvalidFields(newInvalidFields);
      return;
    }

    const requestId = sessionStorage.getItem('userId');
    const applicationRequest = JSON.stringify({
      TemplateName: formData['TemplateName'] || '',
      ResourceGroup: formData['ResourceGroup'] || 2,
      Subscription: formData['Subscription'] || '8f7d862b-5995-401f-a514-7f9f1f9ebae5',
      location: formData['location'] || 'eastus',
      OS: formData['OS'] || 1, // Ensure OS defaults to WindowsServer
      OsImage: formData['OsImage'] || 1,
      Tier: formData['Tier'] || 1,
      VMSizeSeries: formData['VMSizeSeries'] || 'DCadsv5-seriesWindows',
      VMSize: formData['VMSize'] || 'aa263fa2-ba6e-4671-0447-08dd8bb14330',
      Tag: formData['Tag'] || null,
    });

    try {
      const requestBody = {
        templateName: formData['TemplateName'] || '',
        subscription: formData['Subscription'] || '8f7d862b-5995-401f-a514-7f9f1f9ebae5',
        applicationRequest: applicationRequest,
        requester: requestId,
        region: formData['location'] || '1',
        resourceType: Number.parseInt(formData['ResourceType'] || '124'),
      };

      const response = await api.post("/api/Application/createTemplate", requestBody);

      if (response.status === 200 || response.status === 201) {
        toast.success("Template created successfully!");
        navigate('/application_template');
      }
    } catch (error) {
      toast.error("Failed to create template!");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (!field.isDisplay || field.fieldName === "Tag") return null;

    const label = field.isRequired ? `${field.fieldLabel} *` : field.fieldLabel;
    const isError = invalidFields[field.fieldName];

    if (field.fieldType.toLowerCase() === "text") {
      return (
        <Grid item xs={12} sm={6} key={field.fieldId} sx={{ width: '49%' }}>
          <TextField
            label={label}
            fullWidth
            error={isError}
            helperText={isError ? `${label} is required` : ''}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                height: '50px',
                boxShadow: 'none'
              },
              '& .MuiInputLabel-root': {
                shrink: true,
              },
            }}
          />
        </Grid>
      );
    } else if (field.fieldType.toLowerCase() === "dropdown" && field.dropdownValues) {
      const dropdownValues = JSON.parse(field.dropdownValues);
      return (
        <Grid item xs={12} sm={6} key={field.fieldId} sx={{ width: '49%' }}>
          <FormControl fullWidth error={isError}>
            <InputLabel>{label}</InputLabel>
            <Select
              label={label}
              value={formData[field.fieldName] || ""}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
            >
              {dropdownValues.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {isError && <Typography color="error">{`${label} is required`}</Typography>}
          </FormControl>
        </Grid>
      );
    }
    return null;
  };

  return (
    <Layout pageType="sideBar">
      <Box
        sx={{
          flex: 1,
          pl: 2,
          pr: 2,
          pt: 2,
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        {loading && <CircularProgress size={24} sx={{ justifyContent: "center", textAlign: "center", display: 'flex', width: '100%' }} />}
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}
        <Box
          component="form"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {formFields.map((field) => renderField(field))}
            {formData["location"] && vmSizes.length > 0 && (
              <Grid item xs={12} sm={6} sx={{ width: '49%' }}>
                <FormControl fullWidth error={invalidFields['VMSizeSeries']}>
                <InputLabel>VM Size Series</InputLabel>
                  <Select
                    label="VM Size Series"
                    value={formData['VMSizeSeries'] || ''}
                    onChange={(e) => handleChange('VMSizeSeries', e.target.value)}
                  >
                    {vmSizes.map((size) => (
                      <MenuItem key={size.value} value={size.value}>
                        {size.text}
                      </MenuItem>
                    ))}
                  </Select>
                  {invalidFields['VMSizeSeries'] && <Typography color="error">VM Size Series is required</Typography>}
                </FormControl>
              </Grid>
            )}
            {formData["VMSizeSeries"] && vmSizeSeries.length > 0 && (
              <Grid item xs={12} sm={6} sx={{ width: '49%' }}>
                <FormControl fullWidth error={invalidFields['VMSize']}>
                  <InputLabel>VM Size</InputLabel>
                  <Select
                    label="VM Size"
                    value={formData['VMSize'] || ''}
                    onChange={(e) => handleChange('VMSize', e.target.value)}
                  >
                    {vmSizeSeries.map((series) => (
                      <MenuItem key={series.value} value={series.value}>
                        {series.text}
                      </MenuItem>
                    ))}
                  </Select>
                  {invalidFields['VMSize'] && <Typography color="error">VM Size is required</Typography>}
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sx={{ display: 'block', width: '100%' }}>
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }}>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
     
    </Layout>
  );
};

export default ApplicationFormPage;


